/* eslint-disable */

import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Divider, Group, Title, SegmentedControl, Select, Button } from "@mantine/core";
import { GridDots, List, X } from "tabler-icons-react";
import { useGetScopedUsersList } from "api/users/getScopedUsersList";
import { useUser } from "hooks/useUser";
import EmployeeAttendanceTable from "./components/EmployeeAttendanceTable";
import EmployeeAttendanceDetail from "./components/EmployeeAttendanceDetail";
import AttendanceEmployees from "features/attendance-employees";

export default function AttendanceEmployee() {
  const { user } = useUser();
  const [value, setValue] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<{ firstName: string; lastName: string } | null>(null);
  const [searchEmail, setSearchEmail] = useState<string>("");
  const [inputSearchEmail, setInputSearchEmail] = useState<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectingUserRef = useRef(false);

  useEffect(() => {
    const view = localStorage.getItem("EmployeeAttendanceView") ?? "list";
    setValue(view);
  }, []);

  // Debounce search email changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchEmail(inputSearchEmail);
    }, 500); // 500ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputSearchEmail]);

  const handleViewChange = (val: string) => {
    setValue(val);
    localStorage.setItem("EmployeeAttendanceView", val);
  };

  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    if (userId) {
      isSelectingUserRef.current = true;
      setInputSearchEmail("");
      // Reset the flag after a brief delay to allow normal search again
      setTimeout(() => {
        isSelectingUserRef.current = false;
      }, 100);
    } else {
      // Only clear search if user is deselected
      setSearchEmail("");
    }
  };

  // Check if user is manager
  const isManager = user?.role != "member";

  // Fetch scoped users only if user is manager
  const { data: scopedUsersData, isLoading: scopedUsersLoading } = useGetScopedUsersList(
    1,
    100,
    searchEmail || undefined
  );

  // Cache selected user data to avoid depending on API refreshes
  useEffect(() => {
    if (selectedUserId && scopedUsersData?.data) {
      const user = scopedUsersData.data.find((u) => u._id === selectedUserId);
      if (user) {
        setSelectedUser({
          firstName: user.firstName,
          lastName: user.lastName,
        });
      }
    } else if (!selectedUserId) {
      setSelectedUser(null);
    }
  }, [selectedUserId, scopedUsersData?.data]);


  // Format users dropdown data
  const usersDropdownData = useMemo(() => {
    if (!scopedUsersData?.data) return [];
    return scopedUsersData.data.map((u) => ({
      value: u._id,
      label: `${u.firstName} ${u.lastName} (${u.email})`,
    }));
  }, [scopedUsersData]);


  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Group position="apart" align="center" mb="md">
        <Title order={2}>
          Personal Attendence {selectedUser && `- ${selectedUser.firstName} ${selectedUser.lastName}`}
        </Title>

        <Group spacing="md">
          {/* User Selector - Only for managers */}
          {isManager && (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Select
                data={usersDropdownData}
                value={selectedUserId}
                onChange={handleUserSelect}
                onSearchChange={(val) => {
                  setInputSearchEmail(val);
                }}
                placeholder="Select user"
                searchable
                clearable
                disabled={scopedUsersLoading}
                sx={{ width: 250 }}
              />
              {selectedUserId && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    setSelectedUserId(null);
                    setSearchEmail("");
                  }}
                  title="Show my attendance"
                >
                  <X size={16} />
                </Button>
              )}
            </div>
          )}

          {/* View Toggle */}
          <SegmentedControl
            value={value}
            onChange={(e) => handleViewChange(e)}
            data={[
              { label: <GridDots size={16} />, value: "grid" },
              { label: <List size={16} />, value: "list" },
            ]}
            defaultValue="grid"
          />
        </Group>
      </Group>

      <Divider mb="md" />
      {value === "grid" ? (
        <AttendanceEmployees userId={selectedUserId} />
      ) : (
        <>
          {" "}
          <EmployeeAttendanceDetail userId={selectedUserId} />
          <EmployeeAttendanceTable userId={selectedUserId} />
        </>
      )}
    </Box>
  );
}
